import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Loader2, TicketPlus } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { superAdminApi, audToCents, centsToAUD } from "../../lib/events";
import { extractError } from "../../lib/api";

const MODES = [
  { value: "MANUAL_REAL", label: "Manual (real) — counts in sales & capacity" },
  { value: "COMPLIMENTARY", label: "Complimentary — $0, counts in capacity" },
  { value: "INTERNAL_TEST", label: "Internal / Test — hidden from organiser" },
];
const PAYMENT_METHODS = ["cash", "bank_transfer", "external_card", "eftpos", "other"];

function newAttendee() {
  return { name: "", email: "", phone: "" };
}

export default function CreateBookingDialog({ open, onClose, organiserId, eventId, ticketTypes = [], onCreated }) {
  const [recordMode, setRecordMode] = useState("MANUAL_REAL");
  const [ticketTypeId, setTicketTypeId] = useState("");
  const [attendees, setAttendees] = useState([newAttendee()]);
  const [purchaserName, setPurchaserName] = useState("");
  const [purchaserEmail, setPurchaserEmail] = useState("");
  const [purchaserPhone, setPurchaserPhone] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("paid");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [amountAud, setAmountAud] = useState("");
  const [paymentRef, setPaymentRef] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true);
  const [visibleToOrganiser, setVisibleToOrganiser] = useState(true);
  const [allowCapacity, setAllowCapacity] = useState(false);
  const [allowArchived, setAllowArchived] = useState(false);
  const [busy, setBusy] = useState(false);

  const selectableTypes = useMemo(() => ticketTypes || [], [ticketTypes]);

  useEffect(() => {
    if (open) {
      setRecordMode("MANUAL_REAL");
      setTicketTypeId(selectableTypes[0]?.id || "");
      setAttendees([newAttendee()]);
      setPurchaserName(""); setPurchaserEmail(""); setPurchaserPhone("");
      setPaymentStatus("paid"); setPaymentMethod("cash"); setAmountAud(""); setPaymentRef("");
      setAdminNotes(""); setReason(""); setNotify(true); setVisibleToOrganiser(true);
      setAllowCapacity(false); setAllowArchived(false);
    }
  }, [open, selectableTypes]);

  useEffect(() => {
    setNotify(recordMode !== "INTERNAL_TEST");
    setVisibleToOrganiser(recordMode !== "INTERNAL_TEST");
  }, [recordMode]);

  const isPaid = recordMode === "MANUAL_REAL" && paymentStatus === "paid";
  const selectedType = selectableTypes.find((t) => t.id === ticketTypeId);
  const overCapacity = selectedType && recordMode !== "INTERNAL_TEST" && attendees.length > (selectedType.available ?? 0);

  const updateAttendee = (i, field, value) =>
    setAttendees((a) => a.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)));

  const submit = async () => {
    if (!ticketTypeId) return toast.error("Choose a ticket type.");
    if (!purchaserName.trim() || !purchaserEmail.trim()) return toast.error("Purchaser name and email are required.");
    if (attendees.some((a) => !a.name.trim())) return toast.error("Every attendee needs a name.");
    if (overCapacity && !allowCapacity) return toast.error("Not enough capacity — enable the override with a reason.");
    if (overCapacity && allowCapacity && !reason.trim()) return toast.error("A reason is required to override capacity.");
    if (selectedType?.archived && !allowArchived) return toast.error("Ticket type is archived — enable the override.");
    setBusy(true);
    try {
      const payload = {
        idempotency_key: (crypto.randomUUID && crypto.randomUUID()) || `sa-${Date.now()}-${Math.random()}`,
        organiser_id: organiserId,
        event_id: eventId,
        ticket_type_id: ticketTypeId,
        record_mode: recordMode,
        attendees: attendees.map((a) => ({
          name: a.name.trim(),
          email: a.email.trim() || undefined,
          phone: a.phone.trim() || undefined,
        })),
        purchaser_name: purchaserName.trim(),
        purchaser_email: purchaserEmail.trim(),
        purchaser_phone: purchaserPhone.trim() || undefined,
        payment_status: isPaid ? "paid" : "unpaid",
        payment_method: isPaid ? paymentMethod : undefined,
        amount_received_cents: isPaid ? audToCents(amountAud) : undefined,
        manual_payment_reference: isPaid ? (paymentRef.trim() || undefined) : undefined,
        admin_notes: adminNotes.trim() || undefined,
        reason: reason.trim() || undefined,
        notify_buyer: notify,
        visible_to_organiser: visibleToOrganiser,
        allow_capacity_override: allowCapacity,
        allow_archived_override: allowArchived,
      };
      const res = await superAdminApi.createBooking(payload);
      toast.success(`Created ${res.ticket_count} ticket(s)${res.capacity_overridden ? " (capacity overridden)" : ""}.`);
      onCreated && onCreated(res);
      onClose();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sa-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-black tracking-tight flex items-center gap-2">
            <TicketPlus size={20} /> Create booking
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="gt-label mb-1">Record mode</div>
              <select className="gt-input w-full" value={recordMode}
                onChange={(e) => setRecordMode(e.target.value)} data-testid="sa-create-mode">
                {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <div className="gt-label mb-1">Ticket type</div>
              <select className="gt-input w-full" value={ticketTypeId}
                onChange={(e) => setTicketTypeId(e.target.value)} data-testid="sa-create-ticket-type">
                {selectableTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} — {centsToAUD(t.price_cents)} · {t.available} left{t.archived ? " (archived)" : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {recordMode === "INTERNAL_TEST" && (
            <div className="border-2 border-[var(--gt-ink)] bg-yellow-50 p-3 text-xs" data-testid="sa-create-internal-note">
              Internal/Test tickets are hidden from the organiser, excluded from reports, revenue & capacity, and are rejected by live scanners.
            </div>
          )}

          <div className="border border-[var(--gt-line)] p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="gt-label">Attendees ({attendees.length})</div>
              <button type="button" className="gt-btn-ghost text-xs" onClick={() => setAttendees((a) => [...a, newAttendee()])}
                data-testid="sa-create-add-attendee"><Plus size={12} /> Add attendee</button>
            </div>
            <div className="space-y-2">
              {attendees.map((a, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center" data-testid={`sa-create-attendee-${i}`}>
                  <input className="gt-input" placeholder="Name *" value={a.name}
                    onChange={(e) => updateAttendee(i, "name", e.target.value)} data-testid={`sa-create-attendee-name-${i}`} />
                  <input className="gt-input" placeholder="Email" value={a.email}
                    onChange={(e) => updateAttendee(i, "email", e.target.value)} />
                  <input className="gt-input" placeholder="Phone" value={a.phone}
                    onChange={(e) => updateAttendee(i, "phone", e.target.value)} />
                  <button type="button" className="gt-btn-ghost p-2 disabled:opacity-30"
                    disabled={attendees.length === 1}
                    onClick={() => setAttendees((arr) => arr.filter((_, idx) => idx !== i))}
                    data-testid={`sa-create-remove-attendee-${i}`}><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input className="gt-input" placeholder="Purchaser name *" value={purchaserName}
              onChange={(e) => setPurchaserName(e.target.value)} data-testid="sa-create-purchaser-name" />
            <input className="gt-input" placeholder="Purchaser email *" value={purchaserEmail}
              onChange={(e) => setPurchaserEmail(e.target.value)} data-testid="sa-create-purchaser-email" />
            <input className="gt-input" placeholder="Purchaser phone" value={purchaserPhone}
              onChange={(e) => setPurchaserPhone(e.target.value)} />
          </div>

          {recordMode === "MANUAL_REAL" && (
            <div className="border border-[var(--gt-line)] p-3 space-y-3">
              <div className="gt-label">Payment</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <select className="gt-input" value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)} data-testid="sa-create-payment-status">
                  <option value="paid">Paid (external)</option>
                  <option value="unpaid">Unpaid / comp-style</option>
                </select>
                {isPaid && (
                  <select className="gt-input" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} data-testid="sa-create-payment-method">
                    {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                )}
              </div>
              {isPaid && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input className="gt-input" placeholder="Amount received (A$)" value={amountAud}
                    onChange={(e) => setAmountAud(e.target.value)} data-testid="sa-create-amount" />
                  <input className="gt-input" placeholder="Payment reference" value={paymentRef}
                    onChange={(e) => setPaymentRef(e.target.value)} />
                </div>
              )}
              <div className="text-[11px] text-[var(--gt-ink-3)]">Manual payments never create a Stripe charge.</div>
            </div>
          )}

          <textarea className="gt-input w-full" rows={2} placeholder="Internal admin notes (optional)"
            value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} />
          <textarea className="gt-input w-full" rows={2} placeholder="Reason for creating this record"
            value={reason} onChange={(e) => setReason(e.target.value)} data-testid="sa-create-reason" />

          {overCapacity && (
            <label className="flex items-start gap-2 text-sm border-2 border-[var(--gt-accent)] bg-red-50 p-3" data-testid="sa-create-capacity-warning">
              <input type="checkbox" checked={allowCapacity} onChange={(e) => setAllowCapacity(e.target.checked)} data-testid="sa-create-capacity-override" />
              <span>Only {selectedType?.available ?? 0} left but {attendees.length} requested — override capacity (reason required, audited).</span>
            </label>
          )}
          {selectedType?.archived && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={allowArchived} onChange={(e) => setAllowArchived(e.target.checked)} data-testid="sa-create-archived-override" />
              <span>Issue against archived ticket type</span>
            </label>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="sa-create-notify" />
            <span>Email the ticket to the buyer</span>
          </label>
          <label className="flex items-start gap-2 text-sm border border-[var(--gt-line)] p-3" data-testid="sa-create-visible-wrap">
            <input type="checkbox" checked={visibleToOrganiser}
              onChange={(e) => setVisibleToOrganiser(e.target.checked)} data-testid="sa-create-visible" />
            <span>Visible to organiser
              <span className="block text-[11px] text-[var(--gt-ink-3)]">
                When on, this attendee appears in the organiser's attendee/ticket lists, search, exports and attendee reports. Turn off to keep it hidden from the organiser (scanner validity, capacity, payments and platform reconciliation are unaffected).
              </span>
            </span>
          </label>
        </div>

        <DialogFooter>
          <button className="gt-btn-ghost" onClick={onClose} disabled={busy} data-testid="sa-create-cancel">Cancel</button>
          <button className="gt-btn-primary" onClick={submit} disabled={busy} data-testid="sa-create-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <TicketPlus size={14} />} Create booking
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
